import type { RunRowLike } from "@/types/RunRow"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { COMPARE_BY_DIMENSIONS, type CompareByDimension } from "../types"

export type { CompareByDimension } from "../types"

const [
  AGENT_DIMENSION,
  MUTATION_DIMENSION,
  PERSONA_DIMENSION,
  TEST_CASE_DIMENSION,
  METADATA_DIMENSION,
  RUN_DIMENSION,
  TAG_DIMENSION,
  CUSTOM_DIMENSION,
] = COMPARE_BY_DIMENSIONS

export interface CompareBy {
  dimension: CompareByDimension
  metadataKey?: string
  colors: Record<string, string>
  displayLabels?: Record<string, string>
  /** Map from simulation_output_id → custom group id. Required when dimension === "custom". */
  customGroupBySimId?: Record<string, string>
  /** Map from run_id → the run's tags. Required when dimension === "tag";
   *  rows themselves don't carry run-level tags so the caller has to look
   *  them up on the RunRecord and hand them over here. */
  tagsByRunId?: Record<string, string[]>
}

/** Sentinel group key used for simulations not in any group of the active custom dimension. */
const CUSTOM_UNASSIGNED_KEY = "__custom_unassigned__"

/** Sentinel group key used for simulations whose run has no tags. Renders as
 *  a trailing "Untagged" bucket when dimension === "tag". Uppercased so it
 *  cannot collide with any user-authored tag: the tag normalization
 *  contract (see @/tags normalizeTag) lowercases every stored tag, so no
 *  real tag can equal this literal. Must stay in sync with the same
 *  sentinel exported from @/run-detail/compare-by. */
const TAG_UNTAGGED_KEY = "__TAG_UNTAGGED__"

export function getCompareByDisplayLabel(
  key: string,
  compareBy: CompareBy,
): string {
  return compareBy.displayLabels?.[key] ?? key
}

function getGroupKeyForRow(
  row: RunRowLike,
  compareBy: CompareBy,
): string | null {
  if (compareBy.dimension === AGENT_DIMENSION) {
    return row.agent_id ? row.agent_id : null
  }
  if (compareBy.dimension === MUTATION_DIMENSION) {
    return row.mutation_id ?? null
  }
  if (compareBy.dimension === PERSONA_DIMENSION) {
    return row.persona_id ? row.persona_id : null
  }
  if (compareBy.dimension === TEST_CASE_DIMENSION) {
    return row.test_set_row_id ?? null
  }
  if (compareBy.dimension === RUN_DIMENSION) {
    return row.run_id ?? null
  }
  if (compareBy.dimension === METADATA_DIMENSION && compareBy.metadataKey) {
    const raw = row.customer_metadata?.[compareBy.metadataKey]
    return raw !== undefined && raw !== null ? stringifyUnknownValue(raw) : null
  }
  if (compareBy.dimension === CUSTOM_DIMENSION) {
    const simId = row.simulation_output_id
    if (!simId) {
      return null
    }
    return compareBy.customGroupBySimId?.[simId] ?? CUSTOM_UNASSIGNED_KEY
  }
  if (compareBy.dimension === TAG_DIMENSION) {
    // Single-key entry point — returns the row's first tag. Multi-tag
    // consumers use getGroupKeysForRow so a row with two tags contributes
    // to both groups.
    const keys = getGroupKeysForRow(row, compareBy)
    return keys[0] ?? null
  }
  return null
}

/** Multi-key variant. Same as getGroupKeyForRow for single-value dimensions,
 *  but for dimension === "tag" a row with N run-level tags returns N keys so
 *  its metric output appears in every one of its tag groups. Untagged rows
 *  return a one-element array with the sentinel. */
function getGroupKeysForRow(row: RunRowLike, compareBy: CompareBy): string[] {
  if (compareBy.dimension === TAG_DIMENSION) {
    const tags = row.run_id ? (compareBy.tagsByRunId?.[row.run_id] ?? []) : []
    if (tags.length === 0) {
      return [TAG_UNTAGGED_KEY]
    }
    return tags
  }
  const key = getGroupKeyForRow(row, compareBy)
  return key == null ? [] : [key]
}

export function getCompareByGroupKeys(
  runRows: RunRowLike[],
  compareBy: CompareBy,
): string[] {
  const seen = new Set<string>()
  const keys: string[] = []
  for (const row of runRows) {
    for (const key of getGroupKeysForRow(row, compareBy)) {
      if (!seen.has(key)) {
        seen.add(key)
        keys.push(key)
      }
    }
  }
  return keys.sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
  )
}

export function groupMetricOutputsByCompareBy<
  T extends { simulation_output_id: string },
>(
  metricOutputs: T[],
  runRows: RunRowLike[],
  compareBy: CompareBy,
): Map<string, T[]> {
  const rowBySimId = new Map(
    runRows.map((row) => [row.simulation_output_id, row]),
  )
  const groups = new Map<string, T[]>()
  for (const metricOutput of metricOutputs) {
    const row = rowBySimId.get(metricOutput.simulation_output_id)
    if (!row) {
      continue
    }
    for (const key of getGroupKeysForRow(row, compareBy)) {
      const existing = groups.get(key)
      if (existing) {
        existing.push(metricOutput)
      } else {
        groups.set(key, [metricOutput])
      }
    }
  }
  return groups
}
